const normalizeStringList = values => (values || [])
    .map(value => typeof value === 'undefined' || value === null ? '' : String(value))
    .filter(value => value.length > 0);

const normalizeKeyValueMap = entries => entries.reduce((map, {key, value}) => {
    if (key) {
        map[key] = typeof value === 'undefined' ? '' : value;
    }
    return map;
}, {});

const normalizeKeyValueEntries = entries => entries.map(({key, value}) => ({
    key: typeof key === 'undefined' || key === null ? '' : String(key),
    value: typeof value === 'undefined' || value === null ? '' : String(value)
}));

const fieldControls = element => {
    const controls = [];

    const visit = child => {
        if (!child || !child.children) {
            return;
        }

        if (child.tagName === 'INPUT' || child.tagName === 'SELECT') {
            controls.push(child);
        }

        [...child.children].forEach(visit);
    };

    visit(element);
    const namedControls = controls.filter(control => control.name);
    return namedControls.length >= 2 ? namedControls : controls;
};

const createButton = (label, className = 'ui mini compact button') => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    return button;
};

const createIconButton = (label, iconClassName, className = 'ui compact icon button') => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.title = label;
    button.setAttribute('title', label);
    button.setAttribute('aria-label', label);

    const icon = document.createElement('i');
    icon.className = iconClassName;
    button.appendChild(icon);

    return button;
};

const createIconButtonPlaceholder = label => {
    const button = createIconButton(label, 'trash icon', 'ui compact icon button repeatable-remove-placeholder');
    button.disabled = true;
    button.setAttribute('disabled', '');
    button.setAttribute('aria-hidden', 'true');
    button.tabIndex = -1;
    if (button.style) {
        button.style.visibility = 'hidden';
    }
    return button;
};

const createRemoveIcon = label => {
    const icon = document.createElement('i');
    icon.className = 'ui icon trash';
    icon.title = label;
    icon.setAttribute('title', label);
    icon.setAttribute('aria-label', label);
    icon.setAttribute('role', 'button');
    icon.tabIndex = 0;
    return icon;
};

const readStringListValues = container => normalizeStringList([...container.querySelectorAll('input')].map(input => input.value));

const createRepeatableStringList = ({
                                        value = [],
                                        inputName = '',
                                        addLabel = 'Add',
                                        removeLabel = 'Remove',
                                        addBlankRow = false,
                                        editable = true,
                                        onChange = () => {}
                                    } = {}) => {
    const container = document.createElement('div');
    container.className = 'ui form repeatable string-list';

    const render = values => {
        container.innerHTML = '';
        const sourceValues = Array.isArray(values) ? values : [];
        const normalizedValues = normalizeStringList(values);
        const rowValues = editable
            ? (addBlankRow ? [...normalizedValues, ''] : (sourceValues.length > 0 ? sourceValues : ['']))
            : normalizedValues;

        rowValues.forEach((item, index) => {
            const blankInputRow = addBlankRow && index === normalizedValues.length;
            const row = document.createElement('div');
            row.className = addBlankRow && editable && !blankInputRow
                ? 'field repeatable-string-list-row repeatable-focus-remove-row'
                : 'field repeatable-string-list-row';

            const inputGroup = document.createElement('div');
            inputGroup.className = addBlankRow
                ? 'ui fluid input repeatable-string-list-input'
                : 'ui fluid action input';

            const input = document.createElement('input');
            input.name = inputName;
            input.type = 'text';
            input.value = item || '';
            if (!editable) {
                input.disabled = true;
                input.readOnly = true;
                input.setAttribute('disabled', '');
                input.setAttribute('readonly', '');
            } else {
                input.addEventListener('change', () => {
                    const nextValues = readStringListValues(container);
                    onChange(nextValues);
                    if (addBlankRow) {
                        render(nextValues);
                    }
                });
            }
            inputGroup.appendChild(input);

            if (editable && !blankInputRow) {
                const removeRow = () => {
                    row.remove();
                    const nextValues = readStringListValues(container);
                    onChange(nextValues);
                    if (addBlankRow) {
                        render(nextValues);
                    }
                };
                const removeControl = addBlankRow
                    ? createRemoveIcon(removeLabel)
                    : createIconButton(removeLabel, 'trash icon');
                removeControl.addEventListener('mousedown', event => event.preventDefault?.());
                removeControl.addEventListener('click', removeRow);
                removeControl.addEventListener('keydown', event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault?.();
                        removeRow();
                    }
                });
                inputGroup.appendChild(removeControl);
            }

            row.appendChild(inputGroup);
            container.appendChild(row);
        });

        if (editable && !addBlankRow) {
            const addButton = createButton(addLabel);
            addButton.addEventListener('click', () => render([...readStringListValues(container), '']));
            container.appendChild(addButton);
        }
    };

    render(Array.isArray(value) ? value : []);
    return container;
};

const emitKeyValueMap = (container, onChange) => {
    const rows = readKeyValueRows(container);
    onChange(normalizeKeyValueMap(rows));
    return rows;
};

const readKeyValueRows = container => [
    ...container.querySelectorAll('.repeatable-key-value-map-row'),
    ...container.querySelectorAll('.fields')
].map(row => {
    const inputs = fieldControls(row);
    return {
        key: inputs[0]?.value || '',
        value: inputs[1]?.value || ''
    };
});

const createKeyControl = ({key, keyName, keyOptions, keyClassName}) => {
    if (!keyOptions || keyOptions.length === 0) {
        const keyInput = document.createElement('input');
        keyInput.name = keyName;
        keyInput.type = 'text';
        keyInput.value = key || '';
        return keyInput;
    }

    const keySelect = document.createElement('select');
    keySelect.className = keyClassName || 'ui fluid search selection dropdown';
    keySelect.name = keyName;

    const currentValue = key || '';
    const optionValues = keyOptions.map(option => option.value);
    const options = currentValue && !optionValues.includes(currentValue)
        ? [{value: '', name: ''}, {value: currentValue, name: currentValue}, ...keyOptions]
        : [{value: '', name: ''}, ...keyOptions];

    options.forEach(option => {
        const optionElement = document.createElement('option');
        optionElement.value = option.value;
        optionElement.textContent = option.name;
        if (option.value === currentValue) {
            optionElement.selected = true;
            optionElement.setAttribute('selected', '');
        }
        keySelect.appendChild(optionElement);
    });
    keySelect.value = currentValue;

    return keySelect;
};

const createKeyValueMap = ({
                               value = {},
                               keyName = '',
                               valueName = '',
                               keyOptions = [],
                               keyClassName = '',
                               addLabel = 'Add',
                               removeLabel = 'Remove',
                               addBlankRow = false,
                               removeButtonVisibility = 'inline',
                               onRender = () => {},
                               onChange = () => {}
                           } = {}) => {
    const container = document.createElement('div');
    container.className = 'ui form repeatable key-value-map';

    const render = entries => {
        container.innerHTML = '';
        const normalizedEntries = normalizeKeyValueEntries(entries);
        const visibleEntries = addBlankRow
            ? normalizedEntries.filter(({key}) => key)
            : normalizedEntries;
        const rowEntries = addBlankRow
            ? [...visibleEntries, {key: '', value: ''}]
            : (visibleEntries.length > 0 ? visibleEntries : [{key: '', value: ''}]);

        rowEntries.forEach(({key, value: itemValue}, index) => {
            const blankInputRow = addBlankRow && index === visibleEntries.length;
            const focusRemoveButton = removeButtonVisibility === 'focus';
            const hideRemoveButton = removeButtonVisibility === 'none';
            const keyInput = createKeyControl({key, keyName, keyOptions, keyClassName});
            keyInput.addEventListener('change', () => {
                const nextRows = emitKeyValueMap(container, onChange);
                if (addBlankRow) {
                    render(nextRows);
                }
            });

            const valueInput = document.createElement('input');
            valueInput.name = valueName;
            valueInput.type = 'text';
            valueInput.value = typeof itemValue === 'undefined' || itemValue === null ? '' : itemValue;
            valueInput.addEventListener('change', () => {
                const nextRows = emitKeyValueMap(container, onChange);
                if (addBlankRow) {
                    render(nextRows);
                }
            });

            const row = document.createElement('div');
            if (addBlankRow) {
                row.className = focusRemoveButton
                    ? 'field repeatable-key-value-map-row repeatable-focus-remove-row'
                    : 'field repeatable-key-value-map-row';

                const inputGroup = document.createElement('div');
                inputGroup.className = focusRemoveButton
                    ? 'ui fluid labeled input repeatable-key-value-map-field-pair'
                    : 'ui action input';
                inputGroup.appendChild(keyInput);
                inputGroup.appendChild(valueInput);

                if (!blankInputRow && !hideRemoveButton) {
                    const removeRow = () => {
                        row.remove();
                        const nextRows = emitKeyValueMap(container, onChange);
                        render(nextRows);
                    };
                    const removeButton = focusRemoveButton
                        ? createRemoveIcon(removeLabel)
                        : createIconButton(removeLabel, 'trash icon');
                    removeButton.addEventListener('mousedown', event => event.preventDefault?.());
                    removeButton.addEventListener('click', removeRow);
                    removeButton.addEventListener('keydown', event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault?.();
                            removeRow();
                        }
                    });
                    inputGroup.appendChild(removeButton);
                } else if (!focusRemoveButton && !hideRemoveButton) {
                    inputGroup.appendChild(createIconButtonPlaceholder(removeLabel));
                }

                row.appendChild(inputGroup);
            } else {
                row.className = 'fields';

                const keyField = document.createElement('div');
                keyField.className = 'seven wide field';
                keyField.appendChild(keyInput);

                const valueField = document.createElement('div');
                valueField.className = 'seven wide field';
                valueField.appendChild(valueInput);

                row.appendChild(keyField);
                row.appendChild(valueField);

                const actionField = document.createElement('div');
                actionField.className = 'two wide field';
                const removeButton = createIconButton(removeLabel, 'trash icon');
                removeButton.addEventListener('click', () => {
                    row.remove();
                    emitKeyValueMap(container, onChange);
                });
                actionField.appendChild(removeButton);
                row.appendChild(actionField);
            }
            container.appendChild(row);
        });

        onRender(container);

        if (!addBlankRow) {
            const addButton = createButton(addLabel);
            addButton.addEventListener('click', () => render([...readKeyValueRows(container), {key: '', value: ''}]));
            container.appendChild(addButton);
        }
    };

    render(Object.entries(value || {}).map(([key, itemValue]) => ({key, value: itemValue})));
    return container;
};

export {
    createKeyValueMap,
    createRemoveIcon,
    createRepeatableStringList,
    normalizeKeyValueMap,
    normalizeStringList
};
